import { PUBLIC_WS_BASE_URL } from '$env/static/public';
import { generateUUID } from '$lib/helper/uuid';
import type { Message } from '$lib/model/socketMessage';
import { apiStore } from '$lib/stores/api';
import type { Axios } from 'axios';
import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';
import { get, writable } from 'svelte/store';
import { refreshAppData } from './app';
import { DateTime } from 'luxon';
import { now } from './time';

const url = PUBLIC_WS_BASE_URL;
const numberMessagesToKeep = 50;

const debug = false;

let isConnecting = false;
let hasBeenConnected = false;

type SocketStore = {
	socket: Socket;
	connected: boolean;
	authenticated: boolean;
	failedToRecover: boolean;
	messages: SocketIOMessage[];
};

export type SocketIOMessage = {
	id: string;
	direction: 'emit' | 'receive';
	type: string;
	date: DateTime;
	data?: unknown;
};

// SocketEventWatcherStore allows other store to register a function that will be called when a message is received
// It gives other store the ability to self update their state depending on message content.
export type WatcherCallable = (message: Message) => void;
function createSocketEventWatcherStore() {
	const { subscribe, update } = writable<Record<string, WatcherCallable>>({});

	return {
		subscribe,
		register: (key: string, watcher: WatcherCallable) => {
			update((state) => {
				if (state[key]) {
					// Remove the previous watcher if it exists
					delete state[key];
				}
				state[key] = watcher;
				return state;
			});
		},
		unregister: (key: string) => {
			update((state) => {
				delete state[key];
				return state;
			});
		}
	};
}
export const socketEventWatcherStore = createSocketEventWatcherStore();

function createSocketStore() {
	const socket = io(url, {
		autoConnect: false
	});

	const { subscribe, update } = writable<SocketStore>({
		socket,
		connected: false,
		authenticated: false,
		failedToRecover: false,
		messages: []
	});

	socket.io.on('error', (error) => {
		console.error('Failed to connect to socket.', error);
	});

	socket.io.on('reconnect', (attempt) => {
		if (debug) console.log(`Successfully reconnected to socket (attempt ${attempt})`);
	});

	socket.on('connect', () => {
		const message: SocketIOMessage = {
			id: generateUUID(),
			direction: 'receive',
			type: 'connect',
			date: get(now)
		};

		if (hasBeenConnected && !socket.recovered) {
			refreshAppData();
		}

		hasBeenConnected = true;
		isConnecting = false;

		update((state) => ({
			...state,
			messages: [...state.messages.slice(-numberMessagesToKeep), message],
			connected: true
		}));
	});

	socket.on('disconnect', (reason) => {
		const message: SocketIOMessage = {
			id: generateUUID(),
			direction: 'receive',
			type: 'disconnect',
			date: get(now)
		};

		update((state) => ({
			...state,
			messages: [...state.messages.slice(-numberMessagesToKeep), message],
			connected: false,
			authenticated: false
		}));

		console.log(`Disconnected from socket (reason: ${reason})`);
	});

	socket.on('message', (data) => {
		const message: SocketIOMessage = {
			id: generateUUID(),
			direction: 'receive',
			type: 'message',
			date: get(now),
			data
		};

		update((state) => ({
			...state,
			messages: [...state.messages.slice(-numberMessagesToKeep), message]
		}));

		// Notify all watchers registered of the incoming message
		Object.values(get(socketEventWatcherStore)).forEach((watcher) => {
			watcher(data as Message);
		});
	});

	socket.on('connected', () => {
		if (debug) console.log('Connected to socket');

		const message: SocketIOMessage = {
			id: generateUUID(),
			direction: 'receive',
			type: 'connected',
			date: get(now)
		};

		update((state) => ({
			...state,
			messages: [...state.messages.slice(-numberMessagesToKeep), message],
			authenticated: true
		}));
	});

	const connect = async () => {
		// Check connection status before requesting a new token
		if (socket.connected || isConnecting) {
			if (debug) console.log('Already connected, or connecting to socket');
			return;
		}

		isConnecting = true;

		const res = await (get(apiStore) as Axios).get<{ token: string }>('/socket/token');
		const token = res?.data?.token;

		if (!token) {
			console.error("Can't connect to socket. Failed to get token.");
			return;
		}

		const message: SocketIOMessage = {
			id: generateUUID(),
			direction: 'emit',
			type: 'connect',
			date: get(now),
			data: { token }
		};

		update((state) => ({
			...state,
			messages: [...state.messages.slice(-numberMessagesToKeep), message]
		}));

		socket.auth = { token };

		// Recheck connection status before connecting
		if (socket.connected) {
			if (debug) console.log('Already connected to socket');
			return;
		}

		socket.connect();
	};

	const disconnect = () => {
		const message: SocketIOMessage = {
			id: generateUUID(),
			direction: 'emit',
			date: get(now),
			type: 'disconnect'
		};

		update((state) => ({
			...state,
			messages: [...state.messages.slice(-numberMessagesToKeep), message]
		}));

		socket.disconnect();
	};

	return {
		subscribe,
		reconnect: () => {
			if (debug) console.log('Reconnecting to web socket');
			if (socket.connected) {
				socket.disconnect();
			}

			connect();
		},
		connect,
		disconnect
	};
}

export const socketStore = createSocketStore();
